import React from 'react';

import { useMetaDataState } from '@/core/features/a-dynamic-page/hooks/meta-data-state';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import { atom, useAtom } from '@/core/features/store/atom-store';

const ChattyContextCacheAtom = atom<null | string>(null);
const useChattyContextCache = () => useAtom(ChattyContextCacheAtom);

const ChattyEventName = {
    RECEIVE_CONTEXT: 'chattyReceiveContext',
    SEND_CONTEXT: 'chattySendContext',
};

export type ChattyContext = {
    data: Record<string, unknown> | null;
    type: 'PageSwitch';
};

export default function useChattyContext() {
    const { isApp } = useDeviceoutput();
    const chattyContext = useMetaDataState()?.chattyContext ?? null;
    const [chattyContextCache, setChattyContextCache] = useChattyContextCache();
    const timeoutRef = React.useRef<NodeJS.Timeout>();

    const sendChattyContext = React.useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        // 1s delay to give Chatty time to fully initialize the chat before sending context
        timeoutRef.current = setTimeout(() => {
            const nextChattyContextId = JSON.stringify(chattyContext);
            if (nextChattyContextId === chattyContextCache) {
                return;
            }
            const nextChattyContext: ChattyContext = { data: chattyContext, type: 'PageSwitch' };
            setChattyContextCache(nextChattyContextId);
            if (!isApp) {
                window.document.dispatchEvent(
                    new CustomEvent(ChattyEventName.RECEIVE_CONTEXT, {
                        detail: {
                            context: nextChattyContext,
                        },
                    }),
                );
                return;
            }

            if (!window.C24App?.sendContextToChatty) {
                return;
            }
            window.C24App.sendContextToChatty(nextChattyContext);
        }, 1000);
    }, [chattyContext, chattyContextCache, isApp, setChattyContextCache]);

    React.useEffect(() => {
        window.document.addEventListener('c24-chatty-new-session', sendChattyContext);
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            window.document.removeEventListener('c24-chatty-new-session', sendChattyContext);
        };
    }, [sendChattyContext]);

    return { sendChattyContext };
}
